import React from 'react';
import { CaseStudy } from '../types';

export const realtypassCaseStudy: CaseStudy = {
  metadata: {
    id: 'realtypass-web-app',
    slug: 'realtypass-web-app',
    template: 1,
    meta: {
      title: 'RealtyPass Web App | Atini Studio',
      description: 'Atini Studio partnered with RealtyPass to build a tailored web application that addresses the operational challenges of real estate brokers and agents.',
      canonicalUrl: 'https://atinistudio.com/case-study/realtypass-web-app',
      ogImage: 'https://atinistudio.com/assets/atini-logo-colored.svg',
    },
  },
  data: {
    pillLabel: 'Real Estate Platform',
    title: 'RealtyPass Web App',
    description: 'Streamlining Real Estate Transactions, Team Management, and Commission Tracking',
    image: 'realty-pass/add-transaction.png',
    imageAlt: 'RealtyPass Web App',
    story: {
      title: 'The Story',
      content: 'Atini Studio partnered with RealtyPass to build a tailored web application that addresses the operational challenges of real estate brokers and agents. The project was born from firsthand industry experience—an active real estate broker saw critical inefficiencies in how transactions, commissions, and team structures were being managed across traditional systems. The result is a powerful, web-based platform that consolidates transaction processing, document management, team oversight, and commission tracking into one seamless experience. RealtyPass enhances transparency, saves time, and empowers real estate professionals to focus more on client relationships and deal-making.',
    },
    challenge: {
      title: 'The Challenge',
      content: 'Real estate professionals were struggling with fragmented systems, manual commission calculations, and inefficient document management. They needed a unified platform that could consolidate transaction processing, document management, team oversight, and commission tracking into one seamless experience.',
    },
    stats: [
      {
        value: '100%',
        label: 'Consolidated Operations',
        icon: 'users.svg',
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
      {
        value: 'AI-Powered',
        label: 'Document Intelligence',
        icon: 'brain.svg',
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
      {
        value: 'Real-Time',
        label: 'Commission Tracking',
        icon: 'clock.svg',
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
    ],
    journeySteps: [
      {
        number: 1,
        title: 'Discovery & Research',
        description: 'Conducted extensive interviews with real estate brokers and agents to understand pain points in transaction management, commission tracking, and team coordination.',
        icon: 'discovery_icon.svg',
        alt: 'Discovery',
        items: [
          'Industry research & broker interviews',
          'Workflow analysis',
          'Pain point identification',
        ],
      },
      {
        number: 2,
        title: 'Design & Architecture',
        description: 'Designed intuitive workflows for transaction management, commission calculations, and team oversight. Created scalable architecture to accommodate growing teams.',
        icon: 'design_icon.svg',
        alt: 'Design',
        items: [
          'User experience design',
          'System architecture planning',
          'AI document intelligence integration',
        ],
      },
      {
        number: 3,
        title: 'Development',
        description: 'Built the platform using React, Node.js, GraphQL, and MongoDB. Implemented AI-powered document analysis, dynamic commission calculation engine, and comprehensive team management features.',
        icon: 'develop_icon.svg',
        alt: 'Development',
        items: [
          'React web application',
          'GraphQL API development',
          'AI document intelligence',
        ],
      },
      {
        number: 4,
        title: 'Launch & Optimization',
        description: 'Launched the platform with beta brokers, gathered feedback, and optimized workflows based on real-world usage. Ensured seamless integration with existing real estate processes.',
        icon: 'deliver_icon.svg',
        alt: 'Launch',
        items: [
          'Beta testing with brokers',
          'Performance optimization',
          'User feedback integration',
        ],
      },
    ],
    resultsMetrics: [
      {
        label: 'Administrative Friction',
        before: 'High',
        after: 'Reduced',
        change: 'Significant',
        changeType: '-',
        icon: 'trending_down.svg',
      },
      {
        label: 'Tool Consolidation',
        before: 'Multiple Tools',
        after: 'Single Platform',
        change: '100%',
        changeType: '+',
        icon: 'zap.svg',
      },
      {
        label: 'Calculation Accuracy',
        before: 'Manual',
        after: 'Automated',
        change: '99.9%',
        changeType: '+',
        icon: 'earnings.svg',
      },
      {
        label: 'Operational Efficiency',
        before: 'Fragmented',
        after: 'Streamlined',
        change: 'Real-Time',
        changeType: '+',
        icon: 'trending_up.svg',
      },
    ],
    testimonial: {
      name: 'RealtyPass Team',
      role: 'Real Estate Platform',
      quote: 'With RealtyPass, brokers and agents gain a single source of truth for all their operations. What once required spreadsheets, email threads, and multiple tools is now consolidated into one intuitive platform. By reducing administrative friction, automating complex calculations, and providing real-time insights, RealtyPass empowers real estate teams to operate more efficiently, with greater accuracy and confidence. Atini Studio\'s user-first approach ensured the platform was both powerful and approachable—built not just for the tech-savvy, but for any agent ready to level up their business operations.',
    },
    platformFeatures: [
      {
        title: 'Transaction Management',
        description: 'Centralized workspace to track and manage every transaction. Easy upload and organization of contracts, forms, and compliance docs. Status tracking and progress indicators for each deal.',
        stat: '100% centralized',
      },
      {
        title: 'Commission Tracking',
        description: 'Automated calculations for broker-agent commission splits. Configurable models for different team or office arrangements. Transparent reporting and visibility for all stakeholders.',
        stat: '99.9% accuracy',
      },
      {
        title: 'Team & Agent Management',
        description: 'Admin controls to create and manage teams, agents, and permissions. Agent-specific dashboards and transaction history. Performance tracking and customizable access levels.',
        stat: 'Scalable teams',
      },
      {
        title: 'Document Intelligence with AI',
        description: 'AI-powered document analysis for faster classification and tagging. Automatic recognition of key document types (e.g., purchase agreements, disclosures). Reduces manual sorting, increases accuracy, and improves compliance.',
        stat: '90% faster',
      },
    ],
    technologies: [
      {
        name: 'React',
        description: 'Frontend web application',
        logo: 'tech-icons/react.svg',
      },
      {
        name: 'Node.js',
        description: 'Backend server',
        logo: 'tech-icons/nodejs-icon.svg',
      },
      {
        name: 'GraphQL',
        description: 'API layer',
        logo: 'tech-icons/graphql.svg',
      },
      {
        name: 'MongoDB',
        description: 'Database',
        logo: 'tech-icons/mongodb-icon.svg',
      },
    ],
    securityTitle: 'Security & Compliance',
    securityDescription: 'Secure role-based access control, encrypted data storage, compliance with real estate regulations, regular security audits, and 99.9% uptime guarantee.',
  },
};

